import React, { useState } from 'react';
import { CloudActions, CloudErrors } from '@portal/portal-core';
import { navigate } from 'utils';
import { FjButton, FjInput } from 'components/primitives';
import { alertsSelector } from 'selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import AuthContainer from '../auth-container';
import { emailPrevalidation } from '../create-user/pre-validators';

const linkStyle = {
  color: '#156777',
  background: 'transparent',
  borderColor: 'transparent',
};

const ForgotPassword = (): React.ReactElement => {
  // alertsSelector reads dynamic Redux state via lodash `get` (typed `unknown`); it is a
  // map of alert-type keys to truthy flags.
  const alerts = useAppSelector(alertsSelector) as Record<string, unknown>;
  const dispatch = useAppDispatch();

  const [email, setEmail] = useState('');

  const initiateReset = (ev: React.FormEvent<HTMLFormElement>): void => {
    ev.preventDefault();
    if (email) {
      dispatch(CloudActions.generatePasswordResetCode(email));
    }
  };

  const renderEmailInput = (): React.ReactElement => {
    const emailValidityAlerts = emailPrevalidation(email);
    const validator = (val: string): boolean => val.length > 6 && !emailValidityAlerts.length;
    return (
      <FjInput
        id="reset-password-email"
        type="email"
        placeholder="enter email address"
        autoComplete="email"
        value={email}
        onChange={(val: string) => setEmail(val)}
        height={35}
        className="full-width margin-bottom-1"
        borderColor="#156777"
        validator={validator}
        getValidationMessage={() => 'Enter a valid email address.'}
      />
    );
  };

  const renderSubmitButton = (): React.ReactElement => {
    const emailValidityAlerts = emailPrevalidation(email);
    const invalid =
      !email || email.length < 6 || email.length > 254 || Boolean(emailValidityAlerts.length);
    return (
      <FjButton
        defaultStyle={{
          background: '#fff',
          color: '#156777',
          borderColor: '#156777',
        }}
        hoverStyle={{
          background: '#156777',
          color: '#fff',
          borderColor: '#156777',
        }}
        type="submit"
        disabled={invalid}
        className="full-width font-size-1-em margin-bottom-sm margin-top-sm"
      >
        Submit
      </FjButton>
    );
  };

  const renderSignInLink = (): React.ReactElement => (
    <FjButton
      className="return-to-sign-in forgot"
      defaultStyle={linkStyle}
      hoverStyle={linkStyle}
      type="button"
      onClick={() => navigate('sign-in')}
      height={20}
      width={50}
    >
      Return to Sign In
    </FjButton>
  );

  const renderAlerts = (): React.ReactElement | null => {
    if (alerts && alerts[CloudErrors.forgotPasswordErrorTypes.NO_USER_PASSWORD_RESET_CODE]) {
      return (
        <div className="fj-error-container margin-bottom-1">
          <span data-test="forgot-password-error" className="fj-prevalidation">
            Could not generate a reset code with the provided email address.
          </span>
        </div>
      );
    }
    if (alerts && alerts[CloudErrors.forgotPasswordErrorTypes.EXPIRED_PASSWORD_RESET_CODE]) {
      return (
        <div className="fj-error-container margin-bottom-1">
          <span data-test="reset-confirmation-error-expired" className="fj-prevalidation">
            The password reset link has expired. Please submit a new request to reset your password.
          </span>
        </div>
      );
    }
    if (alerts && alerts[CloudErrors.forgotPasswordErrorTypes.INVALID_PASSWORD_RESET_CODE]) {
      return (
        <div className="fj-error-container margin-bottom-1">
          <span data-test="reset-confirmation-error-expired" className="fj-prevalidation">
            The password reset link is invalid. The link may have already been used. Please submit a
            new request to reset your password.
          </span>
        </div>
      );
    }
    return null;
  };

  if (alerts && alerts[CloudErrors.forgotPasswordErrorTypes.SUCCESS_GENERATE_PASSWORD_RESET_CODE]) {
    return (
      <AuthContainer>
        <div className="fj-teal-selectable-font margin-med text-center">
          Request successful! An email has been sent with a link and instructions on how to complete
          this password reset.
        </div>
      </AuthContainer>
    );
  }

  return (
    <AuthContainer title="Reset Account Password">
      <form className="display-flex flex-column flex-align-center" onSubmit={initiateReset}>
        <span className="fj-grey-font margin-bottom-med">
          Please submit your email address so we can send you a password reset link.
        </span>
        {renderEmailInput()}
        {renderAlerts()}
        {renderSubmitButton()}
        {renderSignInLink()}
      </form>
    </AuthContainer>
  );
};

export default ForgotPassword;
